import { useMemo, useState } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { FileItemInfo } from '@/widgets/modals/containers/UploadFiles/types';

import { FileItem } from './FileItem';

type FileListProps = {
  items: FileItemInfo[];
  collapsedItemsCount?: number;
  onItemClick?: (item: FileItemInfo) => void;
  omItemRemove: (id: string) => void;
};

export const FileList = ({
  items,
  collapsedItemsCount = 3,
  onItemClick,
  omItemRemove,
}: FileListProps) => {
  const { t } = useTranslation('widgets/uploaderWidget');
  const [expanded, setExpanded] = useState(false);
  const canExpand = items.length > collapsedItemsCount;

  const visibleItems = useMemo(
    () => (expanded ? items : items.slice(0, collapsedItemsCount)),
    [collapsedItemsCount, expanded, items],
  );

  return (
    <Container>
      {visibleItems.map((item, index) => (
        <FileItem
          fileInfo={item}
          key={item.id}
          onClick={onItemClick}
          onRemove={omItemRemove}
          withDivider={index < visibleItems.length - 1}
          statusText={item.uploaded ? t('uploaded') : undefined}
        />
      ))}
      {canExpand && (
        <ShowAllButton type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? t('hideList') : t('showList')}
          <ToggleIcon name="caret-down" size="s16" className={cn({ expanded })} />
        </ShowAllButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  margin-top: 12px;
  padding: 0 8px 8px;
  border-radius: 4px;
  background: ${COLORS.systemLight100};
`;

const ShowAllButton = styled.button`
  border: none;
  width: 100%;
  height: 32px;
  border-radius: 6px;
  background: ${COLORS.systemLight200};
  color: ${COLORS.systemSky200};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  line-height: 24px;
  font-weight: 700;
  cursor: pointer;
`;

const ToggleIcon = styled(Icon)`
  transition: transform var(--ani-speed) ease-in-out;

  &.expanded {
    transform: rotate(180deg);
  }
`;
